// A Topic's own top-level content is now 3 kinds - TopicElement blocks,
// TopicContainer layout regions, and TopicExerciseTemplate links - all
// interleaved into one ordered sequence (there's no unified ordering on the
// backend, three tables with three independently-numbered `position`
// columns, so this merge is a purely frontend construct - same reasoning as
// exercise-templates/blockList.js's mergeBlocks). Exercise-links are still
// never containerizable (no `container` FK on TopicExerciseTemplate at all -
// see the backend entity) - they only ever appear at the top level
// (containerId === null), same restriction containers themselves already
// have, just enforced by simply never emitting exerciseLink entries for a
// non-null containerId rather than needing any extra guard here.
//
// TopicElementList.jsx additionally guards its own drag-and-drop so an
// exercise-link can never be DROPPED into a container mid-drag either (see
// that file's handleDragOver) - this merge function only governs the
// static, at-rest ordering.

export const TOPIC_ENTRY_KIND_BLOCK = 'block';
export const TOPIC_ENTRY_KIND_CONTAINER = 'container';
export const TOPIC_ENTRY_KIND_EXERCISE = 'exerciseLink';

export function containerIdOf(item) {
    if (!item.container) return null;
    return typeof item.container === 'object' ? item.container.id : item.container;
}

// containerId === null -> top-level scope (blocks with no container, every
// container itself, and every exercise-link). containerId === <id> -> that
// container's own child blocks only (containers don't nest and
// exercise-links can't live in one, so neither comes back in that case).
export function mergeTopicContent({ elements, containers, exerciseLinks, containerId = null }) {
    const blockEntries = (elements || [])
        .filter((el) => containerIdOf(el) === containerId)
        .map((item) => ({ kind: TOPIC_ENTRY_KIND_BLOCK, item }));
    const containerEntries = containerId === null
        ? (containers || []).map((item) => ({ kind: TOPIC_ENTRY_KIND_CONTAINER, item }))
        : [];
    const exerciseEntries = containerId === null
        ? (exerciseLinks || []).map((item) => ({ kind: TOPIC_ENTRY_KIND_EXERCISE, item }))
        : [];
    return [...blockEntries, ...containerEntries, ...exerciseEntries].sort((a, b) => {
        const posDiff = (a.item.position || 0) - (b.item.position || 0);
        if (posDiff !== 0) return posDiff;
        // Tiebreak by id so entries sharing a position (e.g. freshly created
        // blocks) render in a stable, creation order instead of jumping
        // around between re-renders.
        return (a.item.id || 0) - (b.item.id || 0);
    });
}

export function nextTopicContentPosition(mergedList) {
    if (!mergedList || mergedList.length === 0) return 0;
    return Math.max(...mergedList.map((entry) => entry.item.position || 0)) + 1;
}

export function computeTopicContentMove(mergedList, index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= mergedList.length) return null;
    const current = mergedList[index];
    const target = mergedList[targetIndex];
    return [
        { ...current, item: { ...current.item, position: target.item.position } },
        { ...target, item: { ...target.item, position: current.item.position } },
    ];
}
